import { withSubmitGuard } from "../submitGuard.js";
import { esc } from "../escape.js";
import { formatMoney, currencySymbol } from "../currency.js";
import { formatPhone, stripNonDigits, stripNonNameChars, stripNonEmailChars } from "../loyalty.js";

// Strip disallowed characters as they're typed/pasted, keeping the caret
// where the user expects it. `strip` is one of the loyalty.js sanitizers.
export const sanitizeInputLive = function (el, strip) {
  el.addEventListener("input", () => {
    const caretKept = strip(
      el.value.slice(0, el.selectionStart ?? el.value.length),
    ).length;
    const clean = strip(el.value);
    if (clean !== el.value) {
      el.value = clean;
      try { el.setSelectionRange(caretKept, caretKept); } catch {} // not all input types support it
    }
  });
};

// Top-level Loyalty panel: program settings (enable, earn rate, reward
// conversion, streak unit) + stat tiles + the customer list with per-customer
// detail (points ledger + purchase streaks). Customers enroll at checkout or
// via the Add Customer modal here. Follows the Promos panel shell/pattern.
class LoyaltyView {
  _panel        = document.querySelector("#loyaltyPanel");
  _stats        = document.querySelector("#loyaltyStats");
  _list         = document.querySelector("#loyaltyCustomerList");
  _listColumns  = document.querySelector("#loyaltyListColumns");
  _search       = document.querySelector("#loyaltySearchInput");
  _detailModal  = document.querySelector("#loyaltyDetailModal");
  _addModal     = document.querySelector("#loyaltyAddModal");
  _settingsDirty = false;
  _lastSettings  = null; // last-synced settings, used to restore the discount-reward row on Cancel
  _rewardsList     = document.querySelector("#loyaltyRewardsList");
  _rewardsSearch   = document.querySelector("#loyaltyRewardSearch");
  _rewardsResults  = document.querySelector("#loyaltyRewardSearchResults");
  _rewardsPicked   = document.querySelector("#loyaltyRewardPicked");
  _pickedRewardItemId = null; // transient staging for the add-reward flow

  // ── Open / Close ─────────────────────────────────────────────────────────────

  open() {
    this._panel.classList.remove("hidden", "cashflow-exiting");
  }

  close() {
    this._panel.classList.add("cashflow-exiting");
    setTimeout(() => {
      this._panel.classList.add("hidden");
      this._panel.classList.remove("cashflow-exiting");
    }, 220);
  }

  // ── Settings card ────────────────────────────────────────────────────────────

  // Paint the settings controls from saved state and clear the dirty flag.
  syncSettings(settings) {
    this._settingsDirty = false;
    this._lastSettings = settings;
    this._panel.querySelector("#loyaltySettingsActions").classList.add("hidden");

    const toggle = this._panel.querySelector("#loyaltyEnabledToggle");
    toggle.checked = Boolean(settings.loyaltyEnabled);
    this._panel.querySelector("#loyaltyEnabledText").textContent =
      toggle.checked ? "On" : "Off";
    this._panel.querySelector("#loyaltySettingsFields")
      .classList.toggle("loyalty-settings-fields--off", !toggle.checked);

    this._panel.querySelector("#loyaltyEarnRateInput").value = settings.loyaltyEarnRate;
    this._panel.querySelector("#loyaltyEarnAmountInput").value = settings.loyaltyEarnAmount;

    this._panel.querySelectorAll("[data-loyalty-currency]").forEach((el) => {
      el.textContent = currencySymbol();
    });
    this._updateExamples();
    this._renderDiscountReward(settings);
  }

  // Live worked example under the earn inputs. Earning is block-based: N
  // points per FULL M spent (1 pt per 100 → a 250 sale = 2 pts).
  _updateExamples() {
    const earnRate = parseFloat(this._panel.querySelector("#loyaltyEarnRateInput")?.value) || 0;
    const earnAmount = parseFloat(this._panel.querySelector("#loyaltyEarnAmountInput")?.value) || 0;

    const earnEl = this._panel.querySelector("#loyaltyEarnExample");
    if (earnEl && earnRate > 0 && earnAmount > 0) {
      // Pick an example sale sized so the result is a clean, non-zero whole
      // number even for fractional rates (0.1 pt per 1 → a 20.00 sale earns
      // exactly 2). blocksForOnePoint = spend blocks needed for the first
      // whole point; the example uses two points' worth.
      const blocksForOnePoint = Math.ceil(1 / earnRate);
      const exampleBlocks = blocksForOnePoint * 2;
      const exampleSale = earnAmount * exampleBlocks;
      const pts = Math.floor(exampleBlocks * earnRate);
      earnEl.textContent = `Example: a ${formatMoney(exampleSale)} sale earns ${pts} point${pts === 1 ? "" : "s"}`;
    } else if (earnEl) {
      earnEl.textContent = "";
    }
  }

  _getSettingsValues() {
    return {
      loyaltyEnabled: this._panel.querySelector("#loyaltyEnabledToggle").checked,
      loyaltyEarnRate: parseFloat(this._panel.querySelector("#loyaltyEarnRateInput").value),
      loyaltyEarnAmount: parseFloat(this._panel.querySelector("#loyaltyEarnAmountInput").value),
    };
  }

  _markSettingsDirty() {
    this._settingsDirty = true;
    this._panel.querySelector("#loyaltySettingsActions").classList.remove("hidden");
  }

  // ── Discount Reward row (points → $ off) ─────────────────────────────────────
  // Its own compact inline-edit, separate from the Program Settings
  // Save/Discard flow: click the pencil to edit, Save persists immediately.

  _renderDiscountReward(settings) {
    this._panel.querySelector("#loyaltyDiscountPointsText").textContent = settings.loyaltyRedeemPoints;
    this._panel.querySelector("#loyaltyDiscountValueText").textContent = formatMoney(settings.loyaltyRedeemValue);
    this._panel.querySelector("#loyaltyDiscountPointsInput").value = settings.loyaltyRedeemPoints;
    this._panel.querySelector("#loyaltyDiscountValueInput").value = settings.loyaltyRedeemValue;
    this._exitDiscountRewardEdit();
  }

  _getDiscountRewardValues() {
    return {
      loyaltyRedeemPoints: parseInt(this._panel.querySelector("#loyaltyDiscountPointsInput").value),
      loyaltyRedeemValue: parseFloat(this._panel.querySelector("#loyaltyDiscountValueInput").value),
    };
  }

  _enterDiscountRewardEdit() {
    this._panel.querySelector("#loyaltyDiscountRewardDisplay").classList.add("hidden");
    this._panel.querySelector("#loyaltyDiscountRewardEdit").classList.remove("hidden");
    this._panel.querySelector("#loyaltyDiscountEditBtn").classList.add("hidden");
    this._panel.querySelector("#loyaltyDiscountPointsInput").focus();
  }

  _exitDiscountRewardEdit() {
    this._panel.querySelector("#loyaltyDiscountRewardDisplay").classList.remove("hidden");
    this._panel.querySelector("#loyaltyDiscountRewardEdit").classList.add("hidden");
    this._panel.querySelector("#loyaltyDiscountEditBtn").classList.remove("hidden");
  }

  // ── Stats + customer list ────────────────────────────────────────────────────

  // streaks: Map<customerId, { day, week, month }> (each { current, best, dateKeys })
  renderStats(customers, streaks) {
    this._stats.classList.toggle("hidden", customers.length === 0);
    if (!customers.length) return;
    const members = customers.length;
    const outstanding = customers.reduce((s, c) => s + Math.max(0, c.pointsBalance), 0);
    const onStreak = customers.filter((c) => (streaks.get(c.id)?.day?.current ?? 0) >= 2).length;

    const tile = (mod, icon, value, label) => `
      <div class="loyalty-stat loyalty-stat--${mod}">
        <span class="loyalty-stat-icon" aria-hidden="true">${icon}</span>
        <span class="loyalty-stat-text">
          <span class="loyalty-stat-value">${value}</span>
          <span class="loyalty-stat-label">${label}</span>
        </span>
      </div>`;

    this._stats.innerHTML = `
      ${tile("members", "👥", members, `Member${members === 1 ? "" : "s"}`)}
      ${tile("points", "⭐", outstanding.toLocaleString(), "Points outstanding")}
      ${tile("streaks", "🔥", onStreak, "On a 2+ day streak")}`;
  }

  renderCustomers(customers, streaks) {
    this._listColumns?.classList.toggle("hidden", customers.length === 0);
    this._list.innerHTML = customers.length
      ? customers.map((c) => this._rowMarkup(c, streaks.get(c.id))).join("")
      : this._emptyMarkup();
  }

  _rowMarkup(c, streak) {
    const current = streak?.day?.current ?? 0;
    const streakHtml = current >= 2
      ? `<span class="loyalty-row-streak">🔥 ${current} days</span>`
      : "";
    return `
      <button class="loyalty-row" data-id="${c.id}" type="button">
        <span class="loyalty-row-avatar" aria-hidden="true">${esc((c.name || c.phone).charAt(0).toUpperCase())}</span>
        <span class="loyalty-row-main">
          <span class="loyalty-row-name">${esc(c.name || formatPhone(c.phone))}</span>
          <span class="loyalty-row-sub">${c.name ? esc(formatPhone(c.phone)) : "No name"}</span>
        </span>
        <span class="loyalty-row-meta">
          ${streakHtml}
          <span class="loyalty-row-visits">${c.visitCount} visit${c.visitCount === 1 ? "" : "s"}</span>
          <span class="loyalty-row-points">${c.pointsBalance} pts</span>
        </span>
      </button>`;
  }

  _emptyMarkup() {
    return `
      <div class="loyalty-empty">
        <span class="pos-empty-emoji" aria-hidden="true">⭐</span>
        <p class="pos-empty-title">No members yet</p>
        <p class="pos-empty-sub">Enroll customers at checkout by asking for<br/>their phone number, or add one here.</p>
        <button class="btn primary pos-empty-add loyalty-empty-add" type="button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
          Add Customer
        </button>
      </div>`;
  }

  // ── Rewards card ─────────────────────────────────────────────────────────────

  // menuItemsById: Map<itemId, menuItem> so a reward row can show the item's
  // current name (never stored on the reward row itself).
  renderRewards(rewards, menuItemsById) {
    this._rewardsList.innerHTML = rewards.length
      ? rewards.map((r) => this._rewardRowMarkup(r, menuItemsById.get(r.itemId))).join("")
      : `<p class="loyalty-reward-list-empty">No rewards configured yet - search for an item above to add one.</p>`;
  }

  _rewardRowMarkup(reward, item) {
    const name = item ? item.itemName : "(item removed)";
    return `
      <div class="loyalty-reward-row" data-id="${reward.id}">
        <span class="loyalty-reward-row-name">${esc(name)}</span>
        <span class="loyalty-reward-row-cost">${reward.pointsCost} pts</span>
        <label class="loyalty-enable-toggle loyalty-reward-row-toggle">
          <input type="checkbox" class="loyalty-reward-toggle-input" data-id="${reward.id}" ${reward.active ? "checked" : ""} />
          <span class="loyalty-enable-track" aria-hidden="true"></span>
        </label>
        <button type="button" class="loyalty-reward-row-remove" data-id="${reward.id}" aria-label="Remove reward">×</button>
      </div>`;
  }

  // Single-pick variant of the tax-exempt product search in discountView.js -
  // no staged multi-select, clicking a result immediately reveals the
  // points-cost mini-form via showRewardPicked().
  _renderRewardSearchResults(query, menuItems, excludedItemIds) {
    const q = query.trim().toLowerCase();
    if (!q) {
      this._rewardsResults.innerHTML = "";
      this._rewardsResults.classList.add("hidden");
      return;
    }
    const matches = menuItems
      .filter((i) => !excludedItemIds.has(i._id) && i.itemName.toLowerCase().includes(q))
      .slice(0, 20);
    this._rewardsResults.innerHTML = matches.length
      ? matches.map((i) => `
          <li class="loyalty-reward-search-row" data-id="${i._id}" data-name="${esc(i.itemName)}">
            <span class="loyalty-reward-search-row-name">${esc(i.itemName)}</span>
            <span class="loyalty-reward-search-row-meta">${i.category ? `${esc(i.category)} · ` : ""}${formatMoney(i.price)}</span>
          </li>`).join("")
      : `<li class="loyalty-reward-search-row loyalty-reward-search-row--empty">No matching items</li>`;
    this._rewardsResults.classList.remove("hidden");
  }

  showRewardPicked(itemId, itemName) {
    this._pickedRewardItemId = itemId;
    this._panel.querySelector("#loyaltyRewardPickedName").textContent = itemName;
    this._rewardsPicked.classList.remove("hidden");
    this._rewardsResults.classList.add("hidden");
    this._rewardsSearch.value = "";
    const pointsInput = this._panel.querySelector("#loyaltyRewardPointsInput");
    pointsInput.value = "";
    pointsInput.focus();
  }

  hideRewardPicked() {
    this._pickedRewardItemId = null;
    this._rewardsPicked.classList.add("hidden");
  }

  // ── Customer detail modal ────────────────────────────────────────────────────

  // streaks = { day, week, month }, each { current, best, dateKeys }.
  // allTimePoints = lifetime points earned (voided sales excluded).
  showDetail(customer, ledger, streaks, allTimePoints) {
    const memberSince = customer.createdAt
      ? new Date(customer.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : "-";

    const kindMeta = {
      earn: { label: "Earned", cls: "earn" },
      redeem: { label: "Redeemed", cls: "redeem" },
      void_reversal: { label: "Sale voided", cls: "void" },
    };
    const ledgerHtml = ledger.length
      ? ledger.map((tx) => {
          const meta = kindMeta[tx.kind] ?? { label: tx.kind, cls: "other" };
          const when = new Date(tx.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" });
          return `
            <div class="loyalty-tx loyalty-tx--${meta.cls}">
              <span class="loyalty-tx-kind">${meta.label}</span>
              <span class="loyalty-tx-when">${when}</span>
              <span class="loyalty-tx-points">${tx.points > 0 ? "+" : ""}${tx.points} pts</span>
            </div>`;
        }).join("")
      : `<p class="loyalty-detail-none">No points activity yet.</p>`;

    const dates = streaks?.day?.dateKeys ?? [];
    const datesHtml = dates.length
      ? dates.slice(0, 14).map((k) => {
          const [y, m, d] = k.split("-").map(Number);
          const label = new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
          return `<span class="loyalty-date-chip">${label}</span>`;
        }).join("") + (dates.length > 14 ? `<span class="loyalty-date-chip loyalty-date-chip--more">+${dates.length - 14} more</span>` : "")
      : `<p class="loyalty-detail-none">No purchases yet.</p>`;

    const months = ["January","February","March","April","May","June","July",
      "August","September","October","November","December"];
    const profileBits = [];
    if (customer.email) profileBits.push(`<span class="loyalty-profile-bit">✉️ ${esc(customer.email)}</span>`);
    if (customer.birthdayMonth && customer.birthdayDay)
      profileBits.push(`<span class="loyalty-profile-bit">🎂 ${months[customer.birthdayMonth - 1]} ${customer.birthdayDay}</span>`);
    if (customer.note) profileBits.push(`<span class="loyalty-profile-bit loyalty-profile-bit--note">📝 ${esc(customer.note)}</span>`);
    const profileHtml = profileBits.length
      ? `<div class="loyalty-detail-profile">${profileBits.join("")}</div>`
      : "";

    this._detailModal.querySelector("#loyaltyDetailBody").innerHTML = `
      <button class="modal-close-btn" id="loyaltyDetailCloseBtn" type="button">&times;</button>
      <div class="loyalty-detail-head">
        <span class="loyalty-row-avatar loyalty-detail-avatar" aria-hidden="true">${esc((customer.name || customer.phone).charAt(0).toUpperCase())}</span>
        <div class="loyalty-detail-titles">
          <h2 class="edit-form-title">${esc(customer.name || formatPhone(customer.phone))}</h2>
          <p class="loyalty-detail-sub">${esc(formatPhone(customer.phone))} · Member since ${memberSince}</p>
        </div>
        <button type="button" class="btn loyalty-detail-edit-btn" id="loyaltyDetailEditBtn" data-id="${customer.id}">Edit</button>
      </div>
      ${profileHtml}
      <div class="loyalty-detail-stats loyalty-detail-stats--three">
        <div class="loyalty-detail-stat"><span class="loyalty-detail-stat-value">${customer.pointsBalance}</span><span class="loyalty-detail-stat-label">Current points</span></div>
        <div class="loyalty-detail-stat"><span class="loyalty-detail-stat-value">${(allTimePoints ?? 0).toLocaleString()}</span><span class="loyalty-detail-stat-label">All-time points</span></div>
        <div class="loyalty-detail-stat"><span class="loyalty-detail-stat-value">${customer.visitCount}</span><span class="loyalty-detail-stat-label">Visits</span></div>
      </div>
      <h3 class="loyalty-detail-section">Streaks</h3>
      <div class="loyalty-streak-grid">
        ${this._streakTile("Daily", "day", streaks)}
        ${this._streakTile("Weekly", "week", streaks)}
        ${this._streakTile("Monthly", "month", streaks)}
      </div>
      <h3 class="loyalty-detail-section">Purchase dates</h3>
      <div class="loyalty-date-chips">${datesHtml}</div>
      <h3 class="loyalty-detail-section">Points activity</h3>
      <div class="loyalty-tx-list">${ledgerHtml}</div>
    `;
    this._detailModal.classList.remove("hidden");
  }

  _streakTile(label, unit, streaks) {
    const s = streaks?.[unit] ?? { current: 0, best: 0 };
    return `
      <div class="loyalty-streak-tile">
        <span class="loyalty-streak-tile-label">${label}</span>
        <span class="loyalty-streak-tile-current">${s.current > 0 ? "🔥 " : ""}${s.current}</span>
        <span class="loyalty-streak-tile-best">Best: ${s.best}</span>
      </div>`;
  }

  closeDetail() {
    this._detailModal.classList.add("hidden");
    this._detailModal.querySelector("#loyaltyDetailBody").innerHTML = "";
  }

  // ── Add-customer modal ───────────────────────────────────────────────────────

  // Dual-purpose form: Add Customer (customer = null) or Edit (prefilled).
  // Phone is read-only in edit mode - it's the identity key (merge/edit = v2).
  showAddForm(customer = null) {
    const isEdit = customer !== null;
    const months = ["January","February","March","April","May","June","July",
      "August","September","October","November","December"];
    const monthOptions = months
      .map((m, i) => `<option value="${i + 1}"${isEdit && customer.birthdayMonth === i + 1 ? " selected" : ""}>${m}</option>`)
      .join("");

    this._addModal.querySelector("#loyaltyAddForm").innerHTML = `
      <button class="modal-close-btn" id="loyaltyAddCloseBtn" type="button">&times;</button>
      <h2 class="edit-form-title">${isEdit ? "Edit Customer" : "Add Customer"}</h2>
      <div class="edit-field">
        <label for="loyaltyAddPhone">Phone number</label>
        <input type="tel" id="loyaltyAddPhone" placeholder="e.g. 5551234567" autocomplete="off" inputmode="numeric"
          value="${isEdit ? esc(formatPhone(customer.phone)) : ""}" ${isEdit ? "readonly" : 'maxlength="15"'} />
      </div>
      <div class="edit-field">
        <label for="loyaltyAddName">Name <span class="field-optional">(optional)</span></label>
        <input type="text" id="loyaltyAddName" placeholder="e.g. Maria Santos" autocomplete="off"
          value="${isEdit ? esc(customer.name ?? "") : ""}" />
      </div>
      <div class="edit-field">
        <label for="loyaltyAddEmail">Email <span class="field-optional">(optional)</span></label>
        <input type="text" inputmode="email" id="loyaltyAddEmail" placeholder="e.g. maria@email.com" autocomplete="off" spellcheck="false"
          value="${isEdit ? esc(customer.email ?? "") : ""}" />
      </div>
      <div class="edit-field">
        <label for="loyaltyAddBirthdayMonth">Birthday <span class="field-optional">(optional)</span></label>
        <div class="loyalty-birthday-row">
          <select id="loyaltyAddBirthdayMonth">
            <option value="">Month</option>
            ${monthOptions}
          </select>
          <input type="number" id="loyaltyAddBirthdayDay" min="1" max="31" placeholder="Day"
            value="${isEdit && customer.birthdayDay ? customer.birthdayDay : ""}" />
        </div>
      </div>
      <div class="edit-field">
        <label for="loyaltyAddNote">Note <span class="field-optional">(optional, staff only)</span></label>
        <textarea id="loyaltyAddNote" rows="2" placeholder="e.g. Prefers oat milk">${isEdit ? esc(customer.note ?? "") : ""}</textarea>
      </div>
      <div class="adj-form-actions">
        <button type="button" class="btn" id="loyaltyAddCancelBtn">Cancel</button>
        <button type="button" class="btn primary" id="loyaltyAddSaveBtn" data-edit-id="${isEdit ? customer.id : ""}">
          ${isEdit ? "Save" : "Add"}
        </button>
      </div>
    `;
    this._addModal.classList.remove("hidden");
    document.getElementById("loyaltyAddCloseBtn").addEventListener("click", () => this.closeAddForm());
    document.getElementById("loyaltyAddCancelBtn").addEventListener("click", () => this.closeAddForm());
    if (!isEdit) sanitizeInputLive(document.getElementById("loyaltyAddPhone"), stripNonDigits);
    sanitizeInputLive(document.getElementById("loyaltyAddName"), stripNonNameChars);
    sanitizeInputLive(document.getElementById("loyaltyAddEmail"), stripNonEmailChars);
    document.getElementById(isEdit ? "loyaltyAddName" : "loyaltyAddPhone").focus();
  }

  closeAddForm() {
    this._addModal.classList.add("hidden");
    this._addModal.querySelector("#loyaltyAddForm").innerHTML = "";
  }

  // ── Handlers ─────────────────────────────────────────────────────────────────

  _addHandlerOpen(handler) {
    document.addEventListener("click", (e) => {
      if (!e.target.closest("[data-action='loyalty']")) return;
      handler();
    });
  }

  _addHandlerClose(handler) {
    this._panel.querySelector(".loyalty-back").addEventListener("click", () => handler());
    this._detailModal.addEventListener("click", (e) => {
      if (e.target === this._detailModal || e.target.closest("#loyaltyDetailCloseBtn"))
        this.closeDetail();
    });
    this._addModal.addEventListener("click", (e) => {
      if (e.target === this._addModal) this.closeAddForm();
    });
  }

  _addHandlerSettingsChange() {
    const fields = this._panel.querySelector("#loyaltySettingsFields");
    ["#loyaltyEarnRateInput", "#loyaltyEarnAmountInput"].forEach((sel) => {
      this._panel.querySelector(sel).addEventListener("input", () => {
        this._markSettingsDirty();
        this._updateExamples();
      });
    });
    this._panel.querySelector("#loyaltyEnabledToggle").addEventListener("change", (e) => {
      this._panel.querySelector("#loyaltyEnabledText").textContent = e.target.checked ? "On" : "Off";
      fields.classList.toggle("loyalty-settings-fields--off", !e.target.checked);
      this._markSettingsDirty();
    });
  }

  _addHandlerSettingsSave(handler) {
    this._panel.querySelector("#loyaltySettingsSaveBtn").addEventListener("click", (e) => {
      const values = this._getSettingsValues();
      if (isNaN(values.loyaltyEarnRate) || values.loyaltyEarnRate < 0) {
        alert("Please enter a valid number of points earned."); return;
      }
      if (isNaN(values.loyaltyEarnAmount) || values.loyaltyEarnAmount <= 0) {
        alert("Please enter a valid spend amount for earning points."); return;
      }
      withSubmitGuard(e.currentTarget, () => handler(values), { busyText: "Saving…" });
    });
  }

  _addHandlerSettingsDiscard(handler) {
    this._panel.querySelector("#loyaltySettingsDiscardBtn").addEventListener("click", () => handler());
  }

  _addHandlerDiscountRewardEdit() {
    this._panel.querySelector("#loyaltyDiscountEditBtn").addEventListener("click", () => this._enterDiscountRewardEdit());
  }

  _addHandlerDiscountRewardCancel() {
    this._panel.querySelector("#loyaltyDiscountCancelBtn").addEventListener("click", () => {
      this._renderDiscountReward(this._lastSettings ?? this._getDiscountRewardValues());
    });
  }

  _addHandlerDiscountRewardSave(handler) {
    this._panel.querySelector("#loyaltyDiscountSaveBtn").addEventListener("click", (e) => {
      const values = this._getDiscountRewardValues();
      if (isNaN(values.loyaltyRedeemPoints) || values.loyaltyRedeemPoints < 1) {
        alert("Please enter a valid reward point cost."); return;
      }
      if (isNaN(values.loyaltyRedeemValue) || values.loyaltyRedeemValue <= 0) {
        alert("Please enter a valid reward amount."); return;
      }
      withSubmitGuard(e.currentTarget, () => handler(values), { busyText: "Saving…" });
    });
  }

  _addHandlerSearch(handler) {
    this._search.addEventListener("input", () => handler(this._search.value.trim()));
  }

  _addHandlerCustomerClick(handler) {
    this._list.addEventListener("click", (e) => {
      const row = e.target.closest(".loyalty-row");
      if (row) handler(row.dataset.id);
    });
  }

  _addHandlerEditCustomer(handler) {
    this._detailModal.addEventListener("click", (e) => {
      const btn = e.target.closest("#loyaltyDetailEditBtn");
      if (!btn) return;
      this.closeDetail();
      handler(btn.dataset.id);
    });
  }

  _addHandlerAddCustomer(handler) {
    this._panel.querySelector("#loyaltyAddCustomerBtn").addEventListener("click", () => handler());
    // Delegated: the empty-state Add Customer button (re-rendered each render)
    this._list.addEventListener("click", (e) => {
      if (e.target.closest(".loyalty-empty-add")) handler();
    });
  }

  _addHandlerAddCustomerSave(handler) {
    this._addModal.addEventListener("click", (e) => {
      const btn = e.target.closest("#loyaltyAddSaveBtn");
      if (!btn) return;
      const data = {
        id: btn.dataset.editId || null,
        phone: document.getElementById("loyaltyAddPhone")?.value.trim(),
        name: document.getElementById("loyaltyAddName")?.value.trim(),
        email: document.getElementById("loyaltyAddEmail")?.value.trim(),
        birthdayMonth: document.getElementById("loyaltyAddBirthdayMonth")?.value,
        birthdayDay: document.getElementById("loyaltyAddBirthdayDay")?.value,
        note: document.getElementById("loyaltyAddNote")?.value.trim(),
      };
      if (!data.id && !data.phone) { alert("Please enter a phone number."); return; }
      if (data.email && !data.email.includes("@")) { alert("Please enter a valid email address."); return; }
      // Birthday needs both parts (or neither).
      if ((data.birthdayMonth && !data.birthdayDay) || (!data.birthdayMonth && data.birthdayDay)) {
        alert("Please pick both the birthday month and day."); return;
      }
      withSubmitGuard(btn, () => handler(data), { busyText: "Saving…" });
    });
  }

  // ── Rewards handlers ─────────────────────────────────────────────────────────

  _addHandlerRewardSearch(handler) {
    this._rewardsSearch.addEventListener("input", () => handler(this._rewardsSearch.value));
  }

  _addHandlerRewardPick(handler) {
    this._rewardsResults.addEventListener("click", (e) => {
      const row = e.target.closest(".loyalty-reward-search-row[data-id]");
      if (!row) return;
      handler(row.dataset.id, row.dataset.name);
    });
  }

  _addHandlerRewardCancelPick(handler) {
    this._panel.querySelector("#loyaltyRewardCancelPickBtn").addEventListener("click", () => handler());
  }

  _addHandlerRewardAdd(handler) {
    this._panel.querySelector("#loyaltyRewardAddBtn").addEventListener("click", (e) => {
      if (!this._pickedRewardItemId) return;
      const pointsCost = parseInt(this._panel.querySelector("#loyaltyRewardPointsInput").value);
      if (isNaN(pointsCost) || pointsCost < 1) { alert("Please enter a valid point cost."); return; }
      withSubmitGuard(e.currentTarget, () => handler(this._pickedRewardItemId, pointsCost), { busyText: "Adding…" });
    });
  }

  _addHandlerRewardToggle(handler) {
    this._rewardsList.addEventListener("change", (e) => {
      const input = e.target.closest(".loyalty-reward-toggle-input");
      if (!input) return;
      handler(input.dataset.id);
    });
  }

  _addHandlerRewardDelete(handler) {
    this._rewardsList.addEventListener("click", (e) => {
      const btn = e.target.closest(".loyalty-reward-row-remove");
      if (!btn) return;
      handler(btn.dataset.id);
    });
  }
}

export default new LoyaltyView();
